// chainFieldSync.ts
import type { Editor } from '@tiptap/core';

/**
 * Reading and writing chained measurement values inside the live TipTap doc.
 *
 * A chained value is text carrying the `chainField` Mark, whose `class` is the
 * report data key (see `chainFieldMarkup.ts`). That is the only identity the
 * report body has ever had for a measurement — everything here is a walk over
 * the document looking for it.
 */

type ChainOccurrence = {
  key: string;
  from: number;
  to: number;
  text: string;
  warning: string | null;
};

/** The first class on the mark is the data key, matching `classList[0]` in the DOM. */
const keyFromMarkClass = (className: unknown): string | null => {
  if (typeof className !== 'string') {
    return null;
  }
  const first = className.trim().split(/\s+/)[0];
  return first || null;
};

/** Every chained value currently in the document, in document order. */
export function findChainOccurrences(editor: Editor): ChainOccurrence[] {
  const found: ChainOccurrence[] = [];
  if (!editor) {
    return found;
  }

  editor.state.doc.descendants((node, pos) => {
    if (!node.isText) {
      return;
    }
    const mark = node.marks.find(m => m.type.name === 'chainField');
    if (!mark) {
      return;
    }
    const key = keyFromMarkClass(mark.attrs.class);
    if (!key) {
      return;
    }
    found.push({
      key,
      from: pos,
      to: pos + node.nodeSize,
      text: node.text || '',
      warning: mark.attrs['data-chain-warning'] || null,
    });
  });

  return found;
}

/**
 * Write a set of `key -> new value` updates into the document in ONE
 * transaction, updating EVERY occurrence of each key.
 *
 * One transaction matters: chaining produces a whole set of new values at once
 * (BSA plus everything indexed to it). Dispatching them one at a time would put
 * several intermediate states through TipTap's update pipeline — each one
 * triggering an autosave, and briefly showing a document where BSA has moved
 * but the values below it have not.
 *
 * Replacements run back-to-front so that an earlier occurrence's positions are
 * still valid after a later one has changed length.
 *
 * `warnings` is applied the same way: a value marks the field, `null` clears an
 * earlier flag. A key present only in `warnings` keeps its current text.
 *
 * Returns how many occurrences were rewritten.
 */
export function applyChainUpdates(
  editor: Editor,
  updates: Record<string, string>,
  warnings: Record<string, string | null> = {}
): number {
  if (!editor) {
    return 0;
  }

  const occurrences = findChainOccurrences(editor).filter(
    o =>
      Object.prototype.hasOwnProperty.call(updates, o.key) ||
      Object.prototype.hasOwnProperty.call(warnings, o.key)
  );
  if (occurrences.length === 0) {
    return 0;
  }

  const { state } = editor;
  const { tr } = state;
  const chainMarkType = state.schema.marks.chainField;
  if (!chainMarkType) {
    return 0;
  }

  let rewritten = 0;

  // Back-to-front: rewriting a later occurrence cannot invalidate an earlier
  // one's recorded positions.
  for (let i = occurrences.length - 1; i >= 0; i -= 1) {
    const occurrence = occurrences[i];
    const node = state.doc.nodeAt(occurrence.from);
    if (!node || !node.isText) {
      continue;
    }

    const hasValue = Object.prototype.hasOwnProperty.call(updates, occurrence.key);
    const hasWarning = Object.prototype.hasOwnProperty.call(warnings, occurrence.key);
    const nextText = hasValue ? updates[occurrence.key] : occurrence.text;
    const nextWarning = hasWarning ? warnings[occurrence.key] : occurrence.warning;

    if (nextText === occurrence.text && nextWarning === occurrence.warning) {
      continue; // nothing to do — don't dirty the document for no change
    }
    if (!nextText) {
      // Never leave an empty span: it collapses and the value is lost from the
      // report for good, with nothing left to click.
      continue;
    }

    // Rebuild the marks so the chain mark carries the new warning state while
    // every other mark on the text (bold, colour, table styling) is preserved.
    const marks = node.marks.map(mark =>
      mark.type.name === 'chainField'
        ? mark.type.create({ ...mark.attrs, 'data-chain-warning': nextWarning })
        : mark
    );

    tr.replaceWith(occurrence.from, occurrence.to, state.schema.text(nextText, marks));
    rewritten += 1;
  }

  if (rewritten > 0) {
    editor.view.dispatch(tr);
  }

  return rewritten;
}

/**
 * Which value did the clinician just change, comparing every occurrence against the last
 * snapshot?
 *
 * An earlier version snapshotted only the FIRST occurrence of each key, which could not
 * see an edit made to a second copy — editing EF in the Conclusions left the snapshot
 * reading the measurement table's value, so nothing moved. This looks at every occurrence
 * instead, which is what makes the sync work in both directions.
 *
 * Returns null when nothing moved, or when several keys moved at once (a paste, an undo,
 * a template reload) — there is no single source to chain from in that case.
 */
export function findEditedChainValue(
  editor: Editor,
  previous: Record<string, string>
): { key: string; value: string } | null {
  if (!editor || !previous) {
    return null;
  }

  const changed = new Map<string, string>();
  findChainOccurrences(editor).forEach(({ key, text }) => {
    if (!(key in previous)) {
      return; // newly marked field — nothing to compare against yet
    }
    if (text !== previous[key] && text.trim()) {
      changed.set(key, text);
    }
  });

  if (changed.size !== 1) {
    return null;
  }
  const [key, value] = [...changed.entries()][0];
  return { key, value };
}

/**
 * Canonical `key -> value` snapshot for change detection.
 *
 * Every occurrence of a key is written together, so they normally agree. When they do
 * not — the first render of a report whose copies were saved out of sync — the LAST
 * occurrence wins, because the prose copies come after the measurement table and are what
 * a clinician reads as the finding.
 */
export function collectChainSnapshot(editor: Editor): Record<string, string> {
  const values: Record<string, string> = {};
  findChainOccurrences(editor).forEach(({ key, text }) => {
    values[key] = text;
  });
  return values;
}
